import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import Lottie from "../../node_modules/.pnpm/lottie-react@2.4.1_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/lottie-react/build/index.es.js";
import { useToonPlayer } from "./useToonPlayer.js";
import buttonAnimation from "../../assets/toon/lottie/toon_button_reaction.json.js";
import rightAnimation from "../../assets/toon/lottie/toon_outro_right.json.js";
import wrongAnimation from "../../assets/toon/lottie/toon_outro_wrong.json.js";
import rightSoundSrc from "../../assets/toon/sounds/toon_outro_right_sound.mp3.js";
import wrongSoundSrc from "../../assets/toon/sounds/toon_outro_wrong_sound.mp3.js";
import { AnimatePresence } from "../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/components/AnimatePresence/index.js";
import { motion } from "../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import Volume2 from "../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/volume-2.js";
import ChevronLeft from "../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/chevron-left.js";
function ToonOutro({ cards, onComplete, autoPlayAudio = false }) {
  const {
    currentPage,
    direction,
    isFirst,
    isLast,
    goNext,
    goPrev,
    playAudio,
    playSoundEffect
  } = useToonPlayer({ totalPages: cards.length, autoPlayAudio });
  const [answeredPages, setAnsweredPages] = useState(/* @__PURE__ */ new Set());
  const [playingAnimation, setPlayingAnimation] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [showCompleteButton, setShowCompleteButton] = useState(false);
  const card = cards[currentPage];
  const isPageAnswered = answeredPages.has(currentPage);
  useEffect(() => {
    setPlayingAnimation(null);
    setIsCorrect(null);
    setShowCompleteButton(false);
    if (card == null ? void 0 : card.audioUrl) {
      playAudio(card.audioUrl);
    }
  }, [currentPage, card == null ? void 0 : card.audioUrl, playAudio]);
  const checkAnswer = useCallback(
    (answer) => {
      if (!card || isPageAnswered || !answer) return;
      const correct = answer === card.correctAnswer;
      setIsCorrect(correct);
      setPlayingAnimation(answer);
      if (correct) {
        setAnsweredPages((prev) => new Set(prev).add(currentPage));
        setTimeout(() => playSoundEffect(rightSoundSrc), 800);
      } else {
        playSoundEffect(wrongSoundSrc);
      }
    },
    [card, currentPage, isPageAnswered, playSoundEffect]
  );
  const handleAnimationComplete = useCallback(() => {
    if (isCorrect) {
      setShowCompleteButton(true);
    }
    setPlayingAnimation(null);
    setIsCorrect(null);
  }, [isCorrect]);
  const handleNext = useCallback(() => {
    setShowCompleteButton(false);
    goNext();
  }, [goNext]);
  const slideVariants = {
    enter: (dir) => ({
      x: dir === "right" ? 300 : -300,
      opacity: 0
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({
      x: dir === "right" ? -300 : 300,
      opacity: 0
    })
  };
  const getButtonClasses = (answerKey, toon) => {
    const base = "relative h-20 w-full max-w-[50%] rounded-2xl text-white font-semibold text-base active:translate-y-2 transition-all duration-300";
    const isThisAnswer = playingAnimation === answerKey;
    const isCorrectAnswer = toon.correctAnswer === answerKey;
    const pageAnswered = answeredPages.has(currentPage);
    if (pageAnswered && isCorrectAnswer) {
      return `${base} bg-emerald-400 [box-shadow:unset]`;
    }
    if (isThisAnswer && isCorrect === true) {
      return `${base} bg-emerald-400 [box-shadow:unset]`;
    }
    if (isThisAnswer && isCorrect === false) {
      return `${base} bg-red-500 [box-shadow:0px_8px_0px_#D4524A,0px_16px_0px_rgba(0,0,0,0.1)]`;
    }
    return `${base} bg-sky-400 [box-shadow:0px_8px_0px_#2E96CE,0px_16px_0px_rgba(0,0,0,0.1)] hover:opacity-80`;
  };
  const getButtonAnimation = (answerKey, toon) => {
    const isThisAnswer = playingAnimation === answerKey;
    const otherKey = answerKey === "answer_0" ? "answer_1" : "answer_0";
    const isOtherCorrect = playingAnimation === otherKey && isCorrect === true;
    const pageAnswered = answeredPages.has(currentPage);
    const isCorrectAnswer = toon.correctAnswer === answerKey;
    if (isThisAnswer && isCorrect === false) {
      return { y: [-40, 40, -20, 20, 0] };
    }
    if (isThisAnswer && isCorrect === true || pageAnswered && isCorrectAnswer) {
      return { x: answerKey === "answer_0" ? "50%" : "-50%" };
    }
    if (isOtherCorrect || pageAnswered && !isCorrectAnswer) {
      return { y: "200%" };
    }
    return { x: 0, y: 0 };
  };
  return /* @__PURE__ */ jsxs("div", { className: "relative w-full max-w-[720px] md:max-w-[800px] mx-auto", children: [
    /* @__PURE__ */ jsx("div", { className: "relative w-full h-[600px] overflow-hidden bg-white rounded-3xl shadow-xl", children: /* @__PURE__ */ jsx(AnimatePresence, { mode: "wait", custom: direction, children: /* @__PURE__ */ jsxs(
      motion.div,
      {
        custom: direction,
        variants: slideVariants,
        initial: "enter",
        animate: "center",
        exit: "exit",
        transition: { duration: 0.3, ease: "easeInOut" },
        className: "flex flex-col items-center w-full h-full p-8",
        children: [
          (card == null ? void 0 : card.text) && /* @__PURE__ */ jsxs("div", { className: "relative w-full py-4 pl-16 pr-4 font-medium text-center text-gray-700 bg-gray-100 rounded-2xl text-base md:text-lg", children: [
            card.audioUrl && /* @__PURE__ */ jsx(
              "button",
              {
                className: "absolute left-4 top-3 w-10 h-10 flex items-center justify-center rounded-xl bg-orange-400 hover:bg-orange-500 transition-colors",
                onClick: () => playAudio(card.audioUrl),
                children: /* @__PURE__ */ jsx(Volume2, { className: "w-5 h-5 text-white" })
              }
            ),
            card.text
          ] }),
          (card == null ? void 0 : card.imageUrl) && /* @__PURE__ */ jsx(
            "div",
            {
              className: "w-full md:w-[640px] h-[280px] bg-no-repeat bg-center mt-6 bg-contain",
              style: { backgroundImage: `url(${card.imageUrl})` }
            }
          ),
          (card == null ? void 0 : card.answer0) && (card == null ? void 0 : card.answer1) && /* @__PURE__ */ jsx("div", { className: "absolute flex justify-center w-full gap-4 px-8 bottom-8", children: ["answer_0", "answer_1"].map((answerKey) => {
            const text = answerKey === "answer_0" ? card.answer0 : card.answer1;
            return /* @__PURE__ */ jsxs(
              motion.button,
              {
                animate: getButtonAnimation(answerKey, card),
                transition: { duration: 0.5, ease: "linear" },
                onAnimationComplete: playingAnimation === answerKey ? handleAnimationComplete : void 0,
                className: getButtonClasses(answerKey, card),
                onClick: () => checkAnswer(answerKey),
                disabled: isPageAnswered,
                children: [
                  text,
                  playingAnimation === answerKey && /* @__PURE__ */ jsx("div", { className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2", children: /* @__PURE__ */ jsx(
                    Lottie,
                    {
                      animationData: isCorrect ? rightAnimation : wrongAnimation,
                      loop: false,
                      autoplay: true,
                      style: { width: 120, height: 120 }
                    }
                  ) })
                ]
              },
              answerKey
            );
          }) }),
          isLast && showCompleteButton && /* @__PURE__ */ jsx(
            motion.button,
            {
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              transition: { duration: 0.3, delay: 0.3, ease: "easeOut" },
              className: "absolute bottom-8 right-8 px-6 py-2 min-w-[120px] font-bold text-white bg-orange-400 rounded-2xl\n                    [box-shadow:0px_4px_0px_#E88A1C,0px_8px_0px_rgba(0,0,0,0.2)]\n                    hover:bg-orange-500 active:translate-y-1 active:[box-shadow:0px_2px_0px_#E88A1C] transition-all",
              onClick: onComplete,
              children: "완료"
            }
          )
        ]
      },
      currentPage
    ) }) }),
    !isFirst && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: goPrev,
        className: "absolute top-1/2 left-2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center\n              [box-shadow:0px_2px_0px_rgba(0,0,0,0.1)] hover:bg-gray-50 transition-colors",
        children: /* @__PURE__ */ jsx(ChevronLeft, { className: "w-6 h-6 text-gray-500" })
      }
    ),
    isPageAnswered && !isLast && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: handleNext,
        className: "absolute top-[calc(50%-20px)] -right-2 z-10",
        children: /* @__PURE__ */ jsx(
          Lottie,
          {
            animationData: buttonAnimation,
            loop: false,
            autoplay: true,
            style: { width: 80 }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "flex justify-center gap-1.5 mt-4", children: cards.map((_, i) => /* @__PURE__ */ jsx(
      "div",
      {
        className: `w-2.5 h-2.5 rounded-full transition-colors ${i === currentPage ? "bg-orange-400" : answeredPages.has(i) ? "bg-emerald-400" : "bg-lc-gray-300"}`
      },
      i
    )) })
  ] });
}
export {
  ToonOutro
};
//# sourceMappingURL=ToonOutro.js.map
